import { useRef, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigationDirection, type NavigationDirection } from '@/hooks/useNavigationDirection'

interface PageTransitionProps {
  children: React.ReactNode
  /** Enable/disable transitions (default: true) */
  enabled?: boolean
}

// iOS-style push/pop animation
// Forward: new page slides in from right (100%), old page slides left (30%) with shadow
// Back: current page slides out to right (100%), previous page slides in from left (30%)
const pageVariants = {
  enter: (direction: NavigationDirection) => ({
    x: direction === 'forward' ? '100%' : '-30%',
    zIndex: direction === 'forward' ? 2 : 0,
  }),
  center: {
    x: 0,
    zIndex: 1,
  },
  exit: (direction: NavigationDirection) => ({
    x: direction === 'forward' ? '-30%' : '100%',
    zIndex: direction === 'forward' ? 0 : 2,
  }),
}

// iOS uses this specific timing curve
const iosTransition = {
  type: 'tween' as const,
  ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number], // iOS default ease (cubic-bezier)
  duration: 0.35,
}

// Main tabs - no transitions between these
const MAIN_TABS = ['/', '/challenges', '/community', '/shop', '/settings']

/**
 * iOS-style animated page transition wrapper
 * Pages slide simultaneously like native iOS navigation
 */
export function PageTransition({ 
  children, 
  enabled = true,
}: PageTransitionProps) {
  const location = useLocation()
  const { direction } = useNavigationDirection()
  const [isDesktop, setIsDesktop] = useState(false)
  const directionRef = useRef<NavigationDirection>(direction)
  
  // Update direction ref when it changes
  useEffect(() => {
    if (direction !== 'none') {
      directionRef.current = direction
    }
  }, [direction])
  
  // Check for desktop
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])
  
  // No transitions on main tabs or desktop
  const isMainTab = MAIN_TABS.includes(location.pathname)
  
  if (!enabled || isDesktop || isMainTab) {
    return <>{children}</>
  }
  
  return (
    <div className="relative w-full min-h-full overflow-hidden">
      <AnimatePresence 
        initial={false} 
        mode="popLayout"
        custom={directionRef.current}
      >
        <motion.div
          key={location.pathname}
          custom={directionRef.current}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={iosTransition}
          className="w-full"
          style={{
            position: 'relative',
          }}
        >
          {/* Shadow overlay for depth effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0 }}
            exit={{ 
              opacity: directionRef.current === 'forward' ? 0.1 : 0 
            }}
            transition={iosTransition}
            style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.15), transparent 20%)',
              zIndex: 10,
            }}
          />
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

/**
 * Force page transition regardless of route
 */
export function ForcePageTransition({ 
  children, 
  direction = 'forward',
}: {
  children: React.ReactNode
  direction?: NavigationDirection
}) {
  const location = useLocation()
  const [isDesktop, setIsDesktop] = useState(false)
  
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }
    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])
  
  if (isDesktop) {
    return <>{children}</>
  }
  
  return (
    <div className="relative w-full min-h-full overflow-hidden">
      <AnimatePresence initial={false} mode="popLayout" custom={direction}>
        <motion.div
          key={location.pathname}
          custom={direction}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={iosTransition}
          className="w-full"
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
